import { ApolloClient, createHttpLink, from, InMemoryCache } from "@apollo/client";
import type { DefaultOptions } from "@apollo/client";
import { onError } from "@apollo/client/link/error";

type GraphQLRequestBody = {
  query?: string;
  variables?: { preview?: boolean } & Record<string, unknown>;
};

function parseRequestBody(body: unknown): GraphQLRequestBody | null {
  if (typeof body !== "string") return null;
  try {
    return JSON.parse(body) as GraphQLRequestBody;
  } catch {
    return null;
  }
}

async function generateQueryId(query: string, variables?: Record<string, unknown>) {
  const { createHash } = await import("crypto");
  return createHash("sha1")
    .update(query)
    .update(JSON.stringify(variables) || "")
    .digest("hex");
}

/**
 * Server-side fetch that tags each GraphQL request with a stable queryId and
 * stores DatoCMS X-Cache-Tags → queryId mappings in Redis for fine-grained
 * invalidation. Client-side Apollo usage falls through to a plain fetch.
 */
const customFetch: typeof fetch = async (uri, options) => {
  const parsed = parseRequestBody(options?.body);
  const isPreviewRequest = parsed?.variables?.preview === true;

  if (isPreviewRequest) {
    return fetch(uri, {
      ...options,
      cache: "no-store",
      next: { revalidate: 0 },
    });
  }

  // Client components share this Apollo client; skip Next tags + Redis there.
  if (typeof window !== "undefined") {
    return fetch(uri, options);
  }

  const query = parsed?.query;
  if (!query) return fetch(uri, options);

  const queryId = await generateQueryId(query, parsed?.variables);

  const headers = new Headers(options?.headers);
  if (process.env.NODE_ENV === "production") headers.set("X-Cache-Tags", "true");

  const response = await fetch(uri, {
    ...options,
    headers,
    next: { tags: [queryId] },
  });

  const xCacheTags = response.headers.get("X-Cache-Tags");
  if (xCacheTags) {
    const { parseXCacheTagsResponseHeader, storeQueryCacheTags } = await import("utils/cacheTags");
    const cacheTags = parseXCacheTagsResponseHeader(xCacheTags);
    try {
      await storeQueryCacheTags(queryId, cacheTags);
    } catch (error) {
      console.error("Failed to store DatoCMS cache tag mapping", error);
    }
  }

  return response;
};

const httpLink = createHttpLink({
  fetch: customFetch,
  uri: (operation) => {
    return `https://graphql.datocms.com${
      process.env.NEXT_PUBLIC_DATOCMS_ENVIRONMENT
        ? `/environments/${process.env.NEXT_PUBLIC_DATOCMS_ENVIRONMENT}`
        : ""
    }${operation.variables.preview ? "/preview" : ""}`;
  },
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${process.env.DATOCMS_API_TOKEN || process.env.NEXT_PUBLIC_DATOCMS_API_TOKEN}`,
  },
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
    });
  }

  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
  }
});

// Disable caching as it causes issues with locale switching
const defaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: "no-cache",
    errorPolicy: "ignore",
  },
  query: {
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  },
};

export const client = new ApolloClient({
  link: from([errorLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions,
});
